import type { ScanImage, ScanResult } from '../types';
import { signalDeltaForEvent } from '../utils/scanSignals';
import { cleanFinding, isPositiveFinding } from '../utils/scanProgress';

export type SiteScanStepEvent =
  | { kind: 'discovery'; message: string }
  | { kind: 'pages-list'; total: number; urls: string[] }
  | { kind: 'page-fetching'; url: string; elapsedSec?: number }
  | { kind: 'page-done'; url: string; images?: number; title?: string }
  | { kind: 'preview-image'; firstImageUrl: string }
  | { kind: 'images-annotating'; count: number; firstImageUrl: string | null; imageUrls: string[] }
  | { kind: 'images-done'; curated: number }
  | { kind: 'images-curated'; images: ScanImage[] };

type AudienceFactField = 'demographics' | 'primary_geography' | 'use_cases';

export interface AudienceFact {
  field: AudienceFactField;
  values: string[];
  inferred: boolean;
}

const AUDIENCE_FACT_FIELDS: AudienceFactField[] = ['demographics', 'primary_geography', 'use_cases'];

export interface ScanEventCallbacks {
  onScanComplete?: (result: ScanResult) => void;
  onAnalysisReady?: (result: ScanResult) => void;
  onPartialResult?: (partial: Partial<ScanResult>) => void;
  onBrandNameDetected?: (brandName: string) => void;
  onAgentStep?: (agentType: string, label: string, message: string, detail?: Record<string, unknown>) => void;
  onAgentEvent?: (agentType: string, message: string) => void;
  onAgentLifecycle?: (agentType: string, status: 'started' | 'completed' | 'failed') => void;
  onSlowStep?: (agentType: string, elapsedSec: number) => void;
  onSiteScanStep?: (event: SiteScanStepEvent) => void;
  onAudienceFact?: (fact: AudienceFact) => void;
}

interface RouteResult {
  progressDelta: number;
  /** Findings this event carries, counted. Unweighted, unlike progressDelta. */
  signalDelta: number;
  isDone: boolean;
  isFailure: boolean;
  isAnalysisReady?: boolean;
}

export function routeScanEvent(rawData: string, callbacks: ScanEventCallbacks): RouteResult {
  const noop: RouteResult = { progressDelta: 0, signalDelta: 0, isDone: false, isFailure: false };

  let msg: Record<string, unknown>;
  try { msg = JSON.parse(rawData) as Record<string, unknown>; } catch { return noop; }

  switch (msg.type as string) {
    case 'connected':
      return noop;

    case 'scan-complete':
      if (msg.status === 'completed') {
        callbacks.onScanComplete?.(msg.result as ScanResult);
        return { progressDelta: 0, signalDelta: 0, isDone: true, isFailure: false };
      }
      if (msg.partial) callbacks.onPartialResult?.(msg.partial as Partial<ScanResult>);
      return { progressDelta: 0, signalDelta: 0, isDone: false, isFailure: true };
  }

  const agentType = msg.agentType as string | undefined;
  const event = msg.event as Record<string, unknown> | undefined;
  if (!event) return noop;

  const eventMsg = (event.message as string) || '';
  const label = (event.label as string) ?? '';
  const detail = (event.detail ?? {}) as Record<string, unknown>;

  switch (event.type as string) {
    case 'step': {
      if (label === 'brand-name' && agentType === 'pre-fetch') {
        const bn = typeof detail.brandName === 'string' ? detail.brandName : '';
        if (bn) callbacks.onBrandNameDetected?.(bn);
      }
      if (callbacks.onAgentStep && eventMsg) {
        callbacks.onAgentStep(agentType ?? '', label, eventMsg, detail);
      }
      if (callbacks.onAgentEvent && agentType && eventMsg) {
        callbacks.onAgentEvent(agentType, eventMsg);
      }
      if (callbacks.onSlowStep && agentType) {
        const elapsed = typeof detail.elapsedSec === 'number' ? detail.elapsedSec : 0;
        if (elapsed >= 60) callbacks.onSlowStep(agentType, elapsed);
      }
      if (callbacks.onSiteScanStep) {
        _routeSiteScanStep(agentType ?? '', label, detail, eventMsg, callbacks.onSiteScanStep);
      }
      break;
    }
    case 'completed': {
      if (agentType === 'analysis-ready') {
        callbacks.onAnalysisReady?.(event.output as ScanResult);
        return { progressDelta: 0, signalDelta: 0, isDone: false, isFailure: false, isAnalysisReady: true };
      }
      // The image-curator agent's own completion event carries the full curated
      // image array (metadata included) — surface it now rather than waiting for
      // the whole-scan result, so the Visuals step can show it live.
      if (agentType === 'image-curator' && Array.isArray(event.output) && callbacks.onSiteScanStep) {
        callbacks.onSiteScanStep({ kind: 'images-curated', images: event.output as ScanImage[] });
      }
      if (callbacks.onAgentLifecycle && agentType) callbacks.onAgentLifecycle(agentType, 'completed');
      break;
    }
    case 'insight': {
      if (agentType === 'brand-synthesizer' && label === 'audience-fact') {
        const fact = toAudienceFact(detail);
        if (fact) callbacks.onAudienceFact?.(fact);
      }
      break;
    }
    case 'started':
    case 'failed': {
      if (callbacks.onAgentLifecycle && agentType) {
        callbacks.onAgentLifecycle(agentType, event.type as 'started' | 'failed');
      }
      break;
    }
  }

  return {
    progressDelta: _progressDeltaForEventType(event.type as string),
    signalDelta: signalDeltaForEvent({ agentType, eventType: event.type as string, label, detail }),
    isDone: false,
    isFailure: false,
  };
}

function toAudienceFact(detail: Record<string, unknown>): AudienceFact | null {
  const field = detail.field as AudienceFactField;
  if (!AUDIENCE_FACT_FIELDS.includes(field)) return null;
  const values = Array.isArray(detail.values)
    ? detail.values.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    : [];
  if (values.length === 0) return null;
  return { field, values, inferred: detail.inferred === true };
}

function _routeSiteScanStep(
  agentType: string,
  label: string,
  detail: Record<string, unknown>,
  eventMsg: string,
  onSiteScanStep: (e: SiteScanStepEvent) => void,
): void {
  switch (agentType) {
    case 'pre-fetch': {
      if (label === 'preview-image') {
        const url = typeof detail.firstImageUrl === 'string' ? detail.firstImageUrl : '';
        if (url) onSiteScanStep({ kind: 'preview-image', firstImageUrl: url });
      } else if (label === 'checking' || label === 'done') {
        if (eventMsg) onSiteScanStep({ kind: 'discovery', message: eventMsg });
      }
      break;
    }
    case 'social-proof-fetcher': {
      if (['planning', 'fetching', 'done'].includes(label) && isPositiveFinding(eventMsg)) {
        onSiteScanStep({ kind: 'discovery', message: cleanFinding(eventMsg) });
      }
      break;
    }
    case 'scan-planner': {
      if (label === 'planning' && isPositiveFinding(eventMsg)) {
        onSiteScanStep({ kind: 'discovery', message: cleanFinding(eventMsg) });
      }
      break;
    }
    case 'site-page-fetcher': {
      switch (label) {
        case 'fetching':
          onSiteScanStep({ kind: 'pages-list', total: (detail.count as number) ?? 0, urls: (detail.urls as string[]) ?? [] });
          break;
        case 'fetching-page':
          onSiteScanStep({ kind: 'page-fetching', url: detail.url as string, elapsedSec: detail.elapsedSec as number | undefined });
          break;
        case 'page-done':
          onSiteScanStep({
            kind: 'page-done',
            url: detail.url as string,
            images: detail.images as number | undefined,
            title: typeof detail.title === 'string' ? detail.title : undefined,
          });
          break;
      }
      break;
    }
    case 'image-curator': {
      if (label === 'analyzing' && detail.phase === 'annotate') {
        onSiteScanStep({
          kind: 'images-annotating',
          count: (detail.count as number) ?? 0,
          firstImageUrl: (detail.firstImageUrl as string | null) ?? null,
          imageUrls: Array.isArray(detail.imageUrls) ? (detail.imageUrls as string[]).filter(u => typeof u === 'string') : [],
        });
      } else if (label === 'done') {
        onSiteScanStep({ kind: 'images-done', curated: (detail.curated as number) ?? 0 });
      }
      break;
    }
  }
}

function _progressDeltaForEventType(type: string): number {
  switch (type) {
    case 'started':    return 0.6;
    case 'step':       return 0.4;
    case 'tool_call':  return 0.2;
    case 'tool_result':return 0.2;
    case 'log':        return 0.1;
    case 'completed':  return 0.6;
    default:           return 0;
  }
}
